import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, CheckCircle2, Bell, Plus, Trash2, Loader2, KeyRound, Power, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import MTProtoAuthModal from '@/components/admin/MTProtoAuthModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function MTProtoHealthCard() {
  const [health, setHealth] = useState(null);
  const [alertCfg, setAlertCfg] = useState({ admin_chat_ids: [], env_chat_id: null, configured: false });
  const [newChatId, setNewChatId] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [disabledState, setDisabledState] = useState({ disabled: false, source: 'default' });
  const [toggling, setToggling] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [daemon, setDaemon] = useState(null);
  const [daemonEvents, setDaemonEvents] = useState([]);
  const [daemonResetting, setDaemonResetting] = useState(false);

  const load = async () => {
    try {
      const [hRes, aRes] = await Promise.all([
        axios.get(`${API}/mtproto/health`, h()),
        axios.get(`${API}/alerts/config`, h()),
      ]);
      setHealth(hRes.data);
      setAlertCfg(aRes.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let mounted = true;
    const doLoad = async () => {
      try {
        const [hRes, aRes, dRes, dmRes] = await Promise.all([
          axios.get(`${API}/mtproto/health`, h()),
          axios.get(`${API}/alerts/config`, h()),
          axios.get(`${API}/mtproto/disabled`, h()),
          axios.get(`${API}/telegram/mtproto/daemon-status`, h()).catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setHealth(hRes.data);
        setAlertCfg(aRes.data);
        setDisabledState(dRes.data);
        if (dmRes.data) {
          setDaemon(dmRes.data.daemon);
          setDaemonEvents(dmRes.data.recent_events || []);
        }
      } catch { /* ignore */ }
    };
    doLoad();
    const t = setInterval(doLoad, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const resetDaemon = async () => {
    setDaemonResetting(true);
    try {
      const res = await axios.post(`${API}/telegram/mtproto/daemon-reset`, {}, h());
      setDaemon(res.data.daemon);
      toast.success('Daemon reset — monitoring resumed');
    } catch (e) {
      toast.error('Reset failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDaemonResetting(false);
    }
  };

  const toggleDisabled = async () => {
    setToggling(true);
    try {
      const res = await axios.put(`${API}/mtproto/disabled`, { disabled: !disabledState.disabled }, h());
      setDisabledState(s => ({ ...s, disabled: res.data.disabled, source: 'db' }));
      toast.success(res.data.disabled ? 'MTProto DISABLED on this deployment' : 'MTProto ENABLED on this deployment');
    } catch {
      toast.error('Failed to toggle');
    } finally {
      setToggling(false);
    }
  };

  const resetSession = async () => {
    if (!window.confirm('Wipe MTProto session?\n\nThis will:\n• Disconnect active client\n• Delete cached session files\n• Clear DB session records\n\nUse this when AUTH_KEY_DUPLICATED keeps recurring. You\'ll need to re-authenticate after.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/mtproto/reset-session`, {}, h());
      toast.success(`Session wiped. ${res.data.files_deleted?.length || 0} files + ${res.data.db_records_deleted || 0} DB records cleared. Click Re-Authenticate now.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const triggerCheckNow = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/mtproto/health/check-now`, {}, h());
      if (res.data.status === 'connected') toast.success('MTProto reconnected successfully');
      else toast.error('Reconnect failed — re-auth needed');
      await load();
    } catch {
      toast.error('Health check failed');
    } finally {
      setChecking(false);
    }
  };

  const addChatId = async () => {
    const v = newChatId.trim();
    if (!v) return;
    const next = [...alertCfg.admin_chat_ids, v];
    setSaving(true);
    try {
      const res = await axios.put(`${API}/alerts/config`, { admin_chat_ids: next }, h());
      setAlertCfg(c => ({ ...c, admin_chat_ids: res.data.admin_chat_ids, configured: true }));
      setNewChatId('');
      toast.success('Alert chat ID added');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeChatId = async (cid) => {
    const next = alertCfg.admin_chat_ids.filter(x => x !== cid);
    try {
      const res = await axios.put(`${API}/alerts/config`, { admin_chat_ids: next }, h());
      setAlertCfg(c => ({ ...c, admin_chat_ids: res.data.admin_chat_ids, configured: res.data.admin_chat_ids.length > 0 }));
      toast.success('Removed');
    } catch {
      toast.error('Failed');
    }
  };

  const sendTestAlert = async () => {
    try {
      const res = await axios.post(`${API}/alerts/test`, {}, h());
      if (res.data.delivered) toast.success('Test alert delivered');
      else toast.error('No chat IDs configured');
    } catch {
      toast.error('Test failed');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-MY', { hour12: false }); } catch { return iso; }
  };

  const status = health?.status || 'unknown';
  const isHealthy = status === 'connected';
  const statusColor = isHealthy ? '#10b981' : status === 'disconnected' ? '#ef4444' : '#6b7280';

  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 mb-4" data-testid="mtproto-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Activity size={16} style={{ color: statusColor }} />
          <h4 className="text-sm font-bold text-white">MTProto Health & Alerts</h4>
          <Badge style={{ background: `${statusColor}15`, color: statusColor, borderColor: `${statusColor}40` }} className="border text-[10px] uppercase tracking-wider">
            {isHealthy ? <CheckCircle2 size={10} className="mr-1 inline" /> : <AlertTriangle size={10} className="mr-1 inline" />}
            {status}
          </Badge>
          {disabledState.disabled && (
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px] uppercase tracking-wider">
              <Power size={10} className="mr-1 inline" /> Disabled on this deployment
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={toggleDisabled} disabled={toggling} size="sm" variant="outline"
            className={`h-7 text-[10px] ${disabledState.disabled ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10' : 'border-amber-500/40 text-amber-400 hover:bg-amber-500/10'}`}
            data-testid="mtproto-toggle-btn">
            {toggling ? <Loader2 size={11} className="animate-spin mr-1" /> : <Power size={11} className="mr-1" />}
            {disabledState.disabled ? 'Enable Here' : 'Disable Here'}
          </Button>
          <Button onClick={() => setAuthOpen(true)} disabled={disabledState.disabled} size="sm" className="h-7 text-[10px] bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40" data-testid="reauth-btn">
            <KeyRound size={11} className="mr-1" />
            Re-Authenticate
          </Button>
          <Button onClick={resetSession} disabled={resetting || disabledState.disabled} size="sm" variant="outline"
            className="h-7 text-[10px] border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="reset-session-btn">
            {resetting ? <Loader2 size={11} className="animate-spin mr-1" /> : <RotateCcw size={11} className="mr-1" />}
            Reset Session
          </Button>
          <Button onClick={triggerCheckNow} disabled={checking} size="sm" variant="outline" className="h-7 text-[10px]" data-testid="health-check-btn">
            {checking ? <Loader2 size={11} className="animate-spin mr-1" /> : <Activity size={11} className="mr-1" />}
            Check Now
          </Button>
        </div>
      </div>

      {/* Disabled hint */}
      {disabledState.disabled && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 mb-3 text-[11px] text-amber-200/80 leading-relaxed">
          ℹ️ MTProto operations <b>dimatikan</b> di deployment ini. Channel Analytics tak akan sync data baharu. Klik <b>Enable Here</b> kalau ini ialah live production environment yang anda nak guna.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] mb-4">
        <div className="bg-[hsl(var(--card))] rounded-lg p-2.5 border border-[hsl(var(--border))]">
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Last Check</div>
          <div className="text-white font-mono text-[11px]">{fmtTime(health?.last_check_at)}</div>
        </div>
        <div className="bg-[hsl(var(--card))] rounded-lg p-2.5 border border-[hsl(var(--border))]">
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Last Success</div>
          <div className="text-white font-mono text-[11px]">{fmtTime(health?.last_success_at)}</div>
        </div>
        <div className="bg-[hsl(var(--card))] rounded-lg p-2.5 border border-[hsl(var(--border))]">
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Failures (consecutive)</div>
          <div className="font-mono text-[14px] font-bold" style={{ color: (health?.consecutive_failures || 0) > 0 ? '#ef4444' : '#10b981' }}>
            {health?.consecutive_failures || 0}
          </div>
        </div>
      </div>

      <div className="border-t border-[hsl(var(--border))] pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <RotateCcw size={13} className="text-cyan-400" />
            <h5 className="text-[12px] font-bold text-white">Auto-Reconnect Daemon</h5>
            {daemon?.running && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[9px]">RUNNING</Badge>
            )}
            {daemon?.last_status === 'disabled_in_preview' && (
              <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/30 text-[9px]">PREVIEW: IDLE</Badge>
            )}
            {daemon?.manual_action_required && (
              <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-[9px]">NEEDS RE-AUTH</Badge>
            )}
          </div>
          {daemon?.manual_action_required && (
            <Button onClick={resetDaemon} disabled={daemonResetting} size="sm" variant="outline"
              className="h-6 text-[10px] border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10" data-testid="daemon-reset-btn">
              {daemonResetting ? <Loader2 size={11} className="animate-spin mr-1" /> : <RotateCcw size={11} className="mr-1" />}
              Resume After Re-Auth
            </Button>
          )}
        </div>
        {daemon?.manual_action_required && (
          <div className="rounded bg-red-500/10 border border-red-500/30 p-2.5 mb-2 text-[11px] text-red-200 leading-relaxed">
            ⚠ Session expired/revoked: <span className="font-mono">{daemon.manual_action_reason}</span><br />
            Klik <b>Re-Authenticate</b> di atas untuk masukkan OTP code. Lepas dah jaya, klik <b>Resume After Re-Auth</b> supaya daemon teruskan auto-monitor.
          </div>
        )}
        <p className="text-[10px] text-[hsl(var(--text-muted))] mb-2 leading-relaxed">
          Daemon auto-reconnect dengan exponential backoff (5s → 15s → 30s → 60s → 3m → 10m → 30m → 1h).
          Tak perlu masukkan OTP setiap kali — hanya session expired atau revoked sahaja yang perlukan manual re-auth.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-[10px]">
          <div className="bg-[hsl(var(--card))] rounded p-2 border border-[hsl(var(--border))]">
            <div className="uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Last Check</div>
            <div className="font-mono text-white">{fmtTime(daemon?.last_check_at)}</div>
          </div>
          <div className="bg-[hsl(var(--card))] rounded p-2 border border-[hsl(var(--border))]">
            <div className="uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Current Status</div>
            <div className="font-mono" style={{
              color: daemon?.last_status === 'connected' ? '#10b981' :
                     daemon?.last_status === 'terminal' ? '#ef4444' :
                     daemon?.last_status === 'disconnected' ? '#f59e0b' : '#6b7280'
            }}>
              {daemon?.last_status || '—'}
            </div>
          </div>
          <div className="bg-[hsl(var(--card))] rounded p-2 border border-[hsl(var(--border))]">
            <div className="uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Next Check</div>
            <div className="font-mono text-white">{daemon?.next_check_in_seconds != null ? `${daemon.next_check_in_seconds}s` : '—'}</div>
          </div>
          <div className="bg-[hsl(var(--card))] rounded p-2 border border-[hsl(var(--border))]">
            <div className="uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Auto-Reconnects Today</div>
            <div className="font-mono text-emerald-400 font-bold">{daemon?.total_reconnects_today || 0}</div>
          </div>
        </div>
        {daemonEvents.length > 0 && (
          <details className="text-[10px]">
            <summary className="cursor-pointer text-cyan-400 hover:text-cyan-300 select-none mb-1">
              Daemon Event Log ({daemonEvents.length}) — click to expand
            </summary>
            <div className="space-y-1 max-h-[200px] overflow-y-auto bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded p-2">
              {daemonEvents.map((e, i) => (
                <div key={i} className="flex items-start justify-between gap-2 border-b border-[hsl(var(--border))] pb-1 last:border-b-0">
                  <div>
                    <span className={`font-mono uppercase text-[9px] ${e.type === 'auto_reconnect_success' ? 'text-emerald-400' : e.type === 'terminal_error' ? 'text-red-400' : 'text-amber-400'}`}>{e.type}</span>
                    {e.error && <span className="ml-2 text-[hsl(var(--text-muted))]">{e.error}</span>}
                    {e.next_retry_seconds && <span className="ml-2 text-cyan-400">next: {e.next_retry_seconds}s</span>}
                  </div>
                  <span className="text-[hsl(var(--text-muted))] whitespace-nowrap">{fmtTime(e.timestamp)}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      <div className="border-t border-[hsl(var(--border))] pt-3 mt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bell size={13} className="text-[#f59e0b]" />
            <h5 className="text-[12px] font-bold text-white">Telegram Alert Recipients</h5>
            {alertCfg.configured ? (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[9px]">CONFIGURED</Badge>
            ) : (
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[9px]">NOT CONFIGURED</Badge>
            )}
          </div>
          <Button onClick={sendTestAlert} disabled={!alertCfg.configured} size="sm" variant="outline" className="h-6 text-[10px]" data-testid="test-alert-btn">
            Send Test
          </Button>
        </div>
        <p className="text-[10px] text-[hsl(var(--text-muted))] mb-2 leading-relaxed">
          Bila MTProto disconnect lebih 3 kali berturut-turut, bot akan hantar alert ke chat IDs di bawah.
          <br />Cara dapat chat ID: PM bot anda dengan <span className="text-white font-mono">/start</span>, lepas tu hantar mesej ke <span className="text-white font-mono">@userinfobot</span> untuk dapat chat ID. Atau guna group chat ID (negative number).
        </p>

        <div className="flex gap-2 mb-2">
          <Input
            value={newChatId}
            onChange={e => setNewChatId(e.target.value)}
            placeholder="e.g. 1234567890 or -1001234567890"
            className="h-8 text-[11px]"
            data-testid="new-chat-id-input"
          />
          <Button onClick={addChatId} disabled={saving || !newChatId.trim()} size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid="add-chat-id-btn">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {alertCfg.env_chat_id && (
            <Badge variant="outline" className="text-[10px] gap-1 font-mono">
              {alertCfg.env_chat_id} <span className="text-[9px] text-[hsl(var(--text-muted))]">(env)</span>
            </Badge>
          )}
          {alertCfg.admin_chat_ids.length === 0 && !alertCfg.env_chat_id && (
            <span className="text-[11px] text-[hsl(var(--text-muted))]">Tiada chat ID. Add yours so anda dapat alert bila MTProto down.</span>
          )}
          {alertCfg.admin_chat_ids.map(cid => (
            <Badge key={cid} variant="outline" className="text-[10px] gap-1 font-mono pr-0">
              {cid}
              <button onClick={() => removeChatId(cid)} className="ml-1 p-1 hover:bg-red-500/20 rounded-r" data-testid={`remove-chat-${cid}`}>
                <Trash2 size={9} className="text-red-400" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      <MTProtoAuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onAuthSuccess={async () => {
          // Trigger a fresh health check after successful auth
          try {
            await axios.post(`${API}/mtproto/health/check-now`, {}, h());
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
}
